'use client';

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { BreadcrumbItem } from '@/lib/types/pageBuilder';

interface TabBreadcrumbContextValue {
  /** Additional breadcrumb items from active tab */
  tabBreadcrumbs: BreadcrumbItem[];
  /** Update the tab breadcrumbs */
  setTabBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  /** Clear the tab breadcrumbs */
  clearTabBreadcrumbs: () => void;
  /** Whether the client has mounted (for hydration) */
  isMounted: boolean;
  /** First tab's section URL - used as fallback for breadcrumb navigation */
  firstTabSectionUrl: string | null;
  /** Set the first tab's section URL */
  setFirstTabSectionUrl: (url: string | null) => void;
}

const TabBreadcrumbContext = createContext<TabBreadcrumbContextValue | null>(null);

export function TabBreadcrumbProvider({ children }: { children: ReactNode }) {
  const [tabBreadcrumbs, setTabBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [firstTabSectionUrl, setFirstTabSectionUrlState] = useState<string | null>(null);
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);
  // Track if we're doing a tab click (pushState) to avoid clearing breadcrumbs
  const isTabClickRef = useRef(false);

  // Mark as mounted after hydration to prevent breadcrumb flash
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Clear tab breadcrumbs when route changes (actual navigation, not pushState from tab clicks)
  useEffect(() => {
    // Only clear if pathname actually changed AND it's not from a tab click
    if (previousPathnameRef.current !== null && previousPathnameRef.current !== pathname) {
      // If this is a tab click (pushState), don't clear - breadcrumbs were just set
      if (!isTabClickRef.current) {
        setTabBreadcrumbsState([]);
      }
      // Reset the flag
      isTabClickRef.current = false;
    }
    previousPathnameRef.current = pathname;
  }, [pathname]);

  // Also listen for popstate (browser back/forward) to handle URL changes from pushState
  useEffect(() => {
    const handlePopState = () => {
      // Clear breadcrumbs on back/forward navigation
      setTabBreadcrumbsState([]);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setTabBreadcrumbs = useCallback((breadcrumbs: BreadcrumbItem[]) => {
    // Mark that this is from a tab click to prevent pathname effect from clearing
    isTabClickRef.current = true;
    setTabBreadcrumbsState(breadcrumbs);
  }, []);

  const clearTabBreadcrumbs = useCallback(() => {
    setTabBreadcrumbsState([]);
  }, []);

  const setFirstTabSectionUrl = useCallback((url: string | null) => {
    setFirstTabSectionUrlState(url);
  }, []);

  return (
    <TabBreadcrumbContext.Provider value={{ tabBreadcrumbs, setTabBreadcrumbs, clearTabBreadcrumbs, isMounted, firstTabSectionUrl, setFirstTabSectionUrl }}>
      {children}
    </TabBreadcrumbContext.Provider>
  );
}

export function useTabBreadcrumbs() {
  const context = useContext(TabBreadcrumbContext);
  if (!context) {
    // Return default values if used outside provider (for backward compatibility)
    return {
      tabBreadcrumbs: [],
      setTabBreadcrumbs: () => {},
      clearTabBreadcrumbs: () => {},
      isMounted: false,
      firstTabSectionUrl: null,
      setFirstTabSectionUrl: () => {},
    };
  }
  return context;
}
